import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/shared/lib/utils";
import { confirm } from "@/shared/lib/confirm";
import { authClient } from "@/modules/auth/presentation/api/client";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Loader2, MonitorSmartphone, Smartphone } from "lucide-react";
import { toast } from "sonner";

type SessionRow = {
  id: string;
  token: string;
  createdAt: Date | string;
  expiresAt: Date | string;
  ipAddress?: string | null;
  userAgent?: string | null;
  deviceId?: string | null;
  deviceLabel?: string | null;
};

function parseDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

function sessionTitle(row: SessionRow): string {
  if (row.deviceLabel?.trim()) return row.deviceLabel.trim();
  if (row.deviceId?.trim()) {
    return `Device ${row.deviceId.slice(0, 8)}…`;
  }
  const ua = row.userAgent?.trim();
  if (ua) {
    if (/android/i.test(ua)) return "Android";
    if (/iphone|ipad/i.test(ua)) return "iOS";
    if (/windows/i.test(ua)) return "Windows";
    if (/mac os/i.test(ua)) return "macOS";
    return "Browser";
  }
  return "Unknown device";
}

export function ActiveSessionsCard({ className }: { className?: string }) {
  const qc = useQueryClient();
  const { session: currentSession } = useAuthState();
  const currentToken = (
    currentSession as { session?: { token?: string } } | null
  )?.session?.token;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: async () => {
      const res = await authClient.listSessions();
      if (res.error) throw new Error(res.error.message ?? "list failed");
      return (res.data ?? []) as SessionRow[];
    },
  });

  const revokeOne = useMutation({
    mutationFn: (token: string) => authClient.revokeSession({ token }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["auth", "sessions"] });
      await authClient.getSession();
      toast.success("ຍົກເຊສຊັນແລ້ວ");
    },
    onError: () => toast.error("ຍົກເຊສຊັນບໍ່ສຳເລັດ"),
  });

  const revokeOthers = useMutation({
    mutationFn: () => authClient.revokeOtherSessions(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["auth", "sessions"] });
      toast.success("ຍົກເຊສຊັນອື່ນແລ້ວ");
    },
    onError: () => toast.error("ຍົກເຊສຊັນບໍ່ສຳເລັດ"),
  });

  const sessions = data ?? [];
  const busy =
    revokeOne.isPending || revokeOthers.isPending ? revokeOne.variables : null;

  const handleRevoke = async (row: SessionRow) => {
    const isCurrent = row.token === currentToken;
    const ok = await confirm({
      title: isCurrent ? "ອອກຈາກອຸປະກອນນີ້?" : "ຍົກເຊສຊັນນີ້?",
      description: isCurrent
        ? "ທ່ານຈະຖືກອອກຈາກລະບົບບນອຸປະກອນນີ້"
        : "ອຸປະກອນ/ເບຣວເຊີນັ້ນຈະຖືກອອກຈາກບັນຊີ",
      actionText: isCurrent ? "ອອກຈາກລະບົບ" : "ຍົກເຊສຊັນ",
      cancelText: "ຍົກເລີກ",
      ActionProps: isCurrent ? { variant: "destructive" } : undefined,
    });
    if (!ok) return;
    await revokeOne.mutateAsync(row.token);
    if (isCurrent) {
      window.location.href = "/auth/login";
    }
  };

  const handleRevokeOthers = async () => {
    const ok = await confirm({
      title: "ຍົກເຊສຊັນອື່ນທັງໝົດ?",
      description: "ອຸປະກອນອື່ນຈະຖືກອອກຈາກບັນຊີ — ເຊສຊັນປັດຈຸບັນຍັງໃຊ້ໄດ້",
      actionText: "ຍົກເຊສຊັນອື່ນ",
      cancelText: "ຍົກເລີກ",
    });
    if (!ok) return;
    await revokeOthers.mutateAsync();
  };

  return (
    <Card className={cn(className)}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>ເຊສຊັນທີ່ເປີດຢູ່</CardTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            ຈັດການອຸປະກອນທີ່ເຂົ້າສູ່ລະບົບດ້ວຍບັນຊີນີ້
          </p>
        </div>
        {sessions.length > 1 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={revokeOthers.isPending}
            onClick={() => void handleRevokeOthers()}
          >
            {revokeOthers.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              "ຍົກເຊສຊັນອື່ນ"
            )}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="text-muted-foreground size-6 animate-spin" />
          </div>
        ) : isError ? (
          <div className="space-y-2 text-center">
            <p className="text-muted-foreground text-sm">ໂຫຼດລາຍການບໍ່ໄດ້</p>
            <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
              ລອງໃໝ່
            </Button>
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-muted-foreground text-sm">ບໍ່ມີເຊສຊັນ</p>
        ) : (
          sessions.map((row) => {
            const isCurrent = row.token === currentToken;
            const Icon = row.deviceLabel?.includes("App")
              ? Smartphone
              : MonitorSmartphone;
            return (
              <div
                key={row.id}
                className="border-border flex items-start justify-between gap-3 rounded-xl border p-4"
              >
                <div className="flex min-w-0 gap-3">
                  <div className="bg-secondary text-accent flex size-10 shrink-0 items-center justify-center rounded-xl">
                    <Icon className="size-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{sessionTitle(row)}</p>
                      {isCurrent ? (
                        <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] font-bold uppercase">
                          ອຸປະກອນນີ້
                        </span>
                      ) : null}
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">
                      ເຂົ້າ{" "}
                      {formatDistanceToNow(parseDate(row.createdAt), {
                        addSuffix: true,
                      })}
                      {row.ipAddress ? ` · ${row.ipAddress}` : ""}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant={isCurrent ? "destructive" : "outline"}
                  size="sm"
                  disabled={busy === row.token}
                  onClick={() => void handleRevoke(row)}
                >
                  {busy === row.token ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : isCurrent ? (
                    "ອອກ"
                  ) : (
                    "ຍົກ"
                  )}
                </Button>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
